import React, { Fragment, useState } from 'react'
import { arrayOf, func, string, object } from 'prop-types'

import downloadFile from '../../../lib/downloadFile'
import { outgoingShape, templateShape, userShape } from '../../../types'
import {
  APP_STATE,
  CARD_HEIGHT_LARGE,
  CARD_HEIGHT_EXTRA_LARGE,
  TEXT_BIG,
} from '../../../constants'
import { imagesUrl } from '../../../constants/urls'

import Card from '../../shared/Card'
import OutgoingCard from '../../shared/OutgoingCard'
import PageDescription from '../../shared/PageDescription'
import TopButtonsWrapper from '../../shared/TopButtonsWrapper'
import ContentWrapper from '../../shared/ContentWrapper'
import CardGridWrapper from '../../shared/CardGridWrapper'
import Text from '../../shared/Text'
import styled from 'styled-components'
import { auditUrl } from '../../../constants/urls'
import FeedAuditHistoryData from '../../shared/OutgoingCard/FeedAuditHistoryData'

const Spacer = styled.div`
  padding-top: 10px;
`

const calcPercentage = ({ publishedProductCount, totalProductCount }) =>
  publishedProductCount && totalProductCount
    ? Math.round((publishedProductCount / totalProductCount) * 100)
    : 0

const OutgoingCards = ({
  feedId,
  onPublishAll,
  onPauseAll,
  onPublishSelected,
  outgoingListState,
  outgoing,
  templates,
  organisationId,
  notifications,
  feedAuditList,
  auditDataState,
  reloadIndividualAuditData,
  requestAudit,
  user,
  t,
}) => {
  const imageDictionary = (templates || []).reduce((acc, templ) => {
    acc[templ.id] = `${imagesUrl}${templ.imageUrl}`
    return acc
  }, {})
  const [feedAuditHistoryData, setFeedAuditHistoryData] = useState(null)
  const [feedAuditHistoryOpen, setFeedAuditHistoryOpen] = useState(false)
  const [feedAuditHistoryName, setFeedAuditHistoryName] = useState('')

  function enableReloadButton(outgoingId) {
    if (!auditDataState || auditDataState == APP_STATE.FETCHING) {
      return false
    }

    let auditData = feedAuditList.find(item => item.outgoingId === outgoingId)

    if (!auditData && auditDataState != APP_STATE.ERROR) {
      // allow the user to reload the audit data if the outgoing feed has just been created
      return true
    }

    if (
      auditData.State == APP_STATE.FETCHING || // individual card fetching, if errored allow reload
      auditData.auditable == false
    )
      return false // not auditable

    return true
  }

  function enableRequestButton(outgoingId) {
    if (
      !auditDataState ||
      auditDataState == APP_STATE.FETCHING ||
      auditDataState == APP_STATE.ERROR
    ) {
      return false
    }

    let auditData = feedAuditList.find(item => item.outgoingId === outgoingId)
    if (
      !auditData || // outgoing feed has just been created
      auditData.State == APP_STATE.FETCHING || // individual card being reloaded or errored
      auditData.State == APP_STATE.ERROR ||
      auditData.auditable == false ||
      auditData.progressStatus == 2 // in progress
    )
      return false // not auditable
    return true
  }

  function enableAuditHistoryButton(outgoingId) {
    if (
      !auditDataState ||
      auditDataState == APP_STATE.FETCHING ||
      auditDataState == APP_STATE.ERROR
    ) {
      return false
    }

    let auditData = feedAuditList.find(item => item.outgoingId === outgoingId)
    if (
      !auditData || // outgoing feed has just been created
      auditData.State == APP_STATE.FETCHING || // individual card being reloaded or errored
      auditData.State == APP_STATE.ERROR ||
      auditData.auditable == false ||
      auditData.historicalData.length <= 1
    )
      return false // not auditable
    return true
  }

  function enableReportButton(outgoingId) {
    if (
      !auditDataState ||
      auditDataState == APP_STATE.FETCHING ||
      auditDataState == APP_STATE.ERROR
    ) {
      return false
    }

    let auditData = feedAuditList.find(item => item.outgoingId === outgoingId)
    if (
      !auditData || // outgoing feed has just been created
      auditData.State == APP_STATE.FETCHING || // individual card being reloaded or errored
      auditData.State == APP_STATE.ERROR ||
      auditData.auditable == false
    )
      return false // not auditable
    if (auditData.progressStatus == 1) return true // completed
    return false // 'failed', 'in progress', and 'not audited' cannot view report
  }

  function openAuditReport(outgoingId) {
    let auditData = feedAuditList.find(item => item.outgoingId === outgoingId)
    // button will be disabled if no audit data / audit not complete
    if (!auditData || auditData.progressStatus != 1) return

    let url = `${auditUrl}/report_${auditData.auditId}`
    window.open(url, '_blank', 'noreferrer')
  }

  const calcStatus = ({ enabled, publish }) => {
    if (enabled) {
      if (publish) return 'active'
      return 'test'
    }
    return 'paused'
  }

  const openAuditHistory = outgoingFeedId => {
    if (!user.isAdmin) return
    var auditData = feedAuditList.find(a => a.outgoingId == outgoingFeedId)
    var feedData = outgoing.find(a => a.id == outgoingFeedId)

    if (!auditData || !feedData) return

    setFeedAuditHistoryName(feedData.name)
    setFeedAuditHistoryData(auditData.historicalData)
    setFeedAuditHistoryOpen(true)
  }

  const closeFeedAuditHistory = () => {
    setFeedAuditHistoryName('')
    setFeedAuditHistoryData(null)
    setFeedAuditHistoryOpen(false)
  }

  const outgoingCardWithAuditData = (
    id,
    detailUrl,
    name,
    job,
    enabled,
    publish,
    partnerId,
    format
  ) => {
    return (
      <OutgoingCard
        alertLink={`/${organisationId}/reports`}
        alertCount={
          notifications &&
          notifications.filter(x => x.data.outgoingId === id).length
        }
        key={id}
        height={CARD_HEIGHT_EXTRA_LARGE}
        status={calcStatus({ enabled, publish })}
        img={imageDictionary[partnerId]}
        title={name}
        progress={{
          percentage: calcPercentage({
            totalProductCount:
              job && job.stats ? job.stats.totalProductCount : 0,
            publishedProductCount:
              job && job.stats ? job.stats.publishedProductCount : 0,
          }),
          label: t('feed_published'),
        }}
        column={[
          {
            heading: t('number_of_products'),
            description:
              job && job.stats && job.stats.publishedProductCount > 0
                ? t('numericValueInput', {
                    value: job.stats.publishedProductCount,
                  })
                : t('not_available'),
          },
          {
            heading: t('outgoing_last_published'),
            tooltip: t('outgoing_last_published_tooltip'),
            description:
              job && job.stats
                ? t('dateValueInput', {
                    value: new Date(job.stats.finishedAt),
                  })
                : t('not_available'),
          },
        ]}
        buttons={[
          {
            name: t('button_publish'),
            tooltip: t('outgoing_button_publish_tooltip'),
            onClick: () => onPublishSelected(feedId, id),
          },
          {
            name: t('button_edit'),
            tooltip: t('outgoing_button_edit_tooltip'),
            link: detailUrl,
          },
          {
            name: t('button_download'),
            tooltip: t('outgoing_button_last_publish_tooltip'),
            onClick: () =>
              downloadFile(
                t,
                `/jobs/${job.id}/outgoing/${id}/data`,
                `${name}.${format.type}`,
                format.type
              ),
            disabled: !job.id,
          },
        ]}
        auditData={feedAuditList.find(item => item.outgoingId === id)}
        auditButtons={[
          {
            name: t('reload'),
            tooltip: t('feed_audit_reload_tooltip'),
            onClick: () => reloadIndividualAuditData(id),
            disabled: !enableReloadButton(id),
          },
          {
            name: t('feed_audit_request_audit'),
            tooltip: t('feed_audit_request_outgoing_tooltip'),
            onClick: () => requestAudit(id),
            disabled: !enableRequestButton(id),
          },
          {
            name: t('feed_audit_view_report'),
            tooltip: t('feed_audit_view_report_tooltip'),
            onClick: () => openAuditReport(id),
            disabled: !enableReportButton(id),
          },
          {
            name: t('feed_audit_view_history'),
            tooltip: t('feed_audit_view_history_tooltip'),
            onClick: () => openAuditHistory(id),
            disabled: !enableAuditHistoryButton(id),
            // buttonIcon: "history",
          },
        ]}
        auditDataState={auditDataState}
      />
    )
  }

  const outgoingCardWithoutAuditData = (
    id,
    detailUrl,
    name,
    job,
    enabled,
    publish,
    partnerId,
    format
  ) => {
    return (
      <Card
        alertLink={`/${organisationId}/reports`}
        alertCount={
          notifications &&
          notifications.filter(x => x.data.outgoingId === id).length
        }
        key={id}
        height={CARD_HEIGHT_LARGE}
        status={calcStatus({ enabled, publish })}
        img={imageDictionary[partnerId]}
        title={name}
        progress={{
          percentage: calcPercentage({
            totalProductCount:
              job && job.stats ? job.stats.totalProductCount : 0,
            publishedProductCount:
              job && job.stats ? job.stats.publishedProductCount : 0,
          }),
          label: t('feed_published'),
        }}
        column={[
          {
            heading: t('number_of_products'),
            description:
              job && job.stats && job.stats.publishedProductCount > 0
                ? t('numericValueInput', {
                    value: job.stats.publishedProductCount,
                  })
                : t('not_available'),
          },
          {
            heading: t('outgoing_last_published'),
            tooltip: t('outgoing_last_published_tooltip'),
            description:
              job && job.stats
                ? t('dateValueInput', {
                    value: new Date(job.stats.finishedAt),
                  })
                : t('not_available'),
          },
        ]}
        buttons={[
          {
            name: t('button_publish'),
            tooltip: t('outgoing_button_publish_tooltip'),
            onClick: () => onPublishSelected(feedId, id),
          },
          {
            name: t('button_edit'),
            tooltip: t('outgoing_button_edit_tooltip'),
            link: detailUrl,
          },
          {
            name: t('button_download'),
            tooltip: t('outgoing_button_last_publish_tooltip'),
            onClick: () =>
              downloadFile(
                t,
                `/jobs/${job.id}/outgoing/${id}/data`,
                `${name}.${format.type}`,
                format.type
              ),
            disabled: !job.id,
          },
        ]}
      />
    )
  }

  return (
    <Fragment>
      <PageDescription
        heading={t('outgoing')}
        description={t('outgoing_desc')}
      />
      <TopButtonsWrapper
        buttons={[
          {
            'data-id': 'new-outgoing',
            name: 'new_outgoing',
            tooltip: t('outgoing_button_new_outgoing_tooltip'),
            link: `/${organisationId}/feeds/${feedId}/outgoing/new`,
          },
          {
            name: 'outgoing_publish_active',
            onClick: () => onPublishAll(),
            tooltip: t('outgoing_button_publish_all_tooltip'),
            disabled: outgoing && !outgoing.length,
          },
          {
            name: 'button_pause_all',
            onClick: () => onPauseAll(),
            tooltip: t('outgoing_button_pause_all_tooltip'),
            disabled: outgoing && !outgoing.length,
          },
        ]}
      />
      <FeedAuditHistoryData
        onClose={closeFeedAuditHistory}
        visible={feedAuditHistoryOpen}
        historyData={feedAuditHistoryData}
        feedName={feedAuditHistoryName}
      />
      <Spacer />
      {outgoingListState === APP_STATE.DONE && (
        <ContentWrapper>
          <CardGridWrapper>
            {outgoing.length === 0 && (
              <Text size={TEXT_BIG}>{t('no_outgoings_available')}</Text>
            )}
            {outgoing.map(
              ({
                id,
                detailUrl,
                name,
                job,
                enabled,
                publish,
                partnerId,
                format,
              }) =>
                user.isAdmin
                  ? outgoingCardWithAuditData(
                      id,
                      detailUrl,
                      name,
                      job,
                      enabled,
                      publish,
                      partnerId,
                      format
                    )
                  : outgoingCardWithoutAuditData(
                      id,
                      detailUrl,
                      name,
                      job,
                      enabled,
                      publish,
                      partnerId,
                      format
                    )
            )}
          </CardGridWrapper>
        </ContentWrapper>
      )}
    </Fragment>
  )
}

OutgoingCards.propTypes = {
  feedId: string.isRequired,
  onPublishAll: func.isRequired,
  onPauseAll: func,
  onPublishSelected: func.isRequired,
  outgoingListState: string,
  notifications: arrayOf(object),
  outgoing: arrayOf(outgoingShape).isRequired,
  organisationId: string.isRequired,
  templates: arrayOf(templateShape),
  user: userShape.isRequired,
}

export default OutgoingCards
